import React from 'react';

import { IAmpPropsTag } from '../../../typings/amp';
import { ColorSchema, ColorSchemaToColor, ComponentSize, FontSize } from '../../../typings/design-tokens';
import { getFooter, getHeader } from '../../../utils/match-header-footer';
// FIXME: LC 2021-02-17 - COMPUTE match until new 'FullTimeAfterShootOut' and 'FullTimeAfterShootOut' statuses will be provided by BE!
import {
  addIsLoseOnTeams,
  computeFullTime,
  getMatchResult,
  getMatchState,
  isEnded,
  isKnockout,
  isSecondLeg,
} from '../../../utils/match-status';
import { slugifyPascalCase } from '../../../utils/slugify';
import { applyColorSchema, makeClassNames } from '../../../utils/styles';
import ResultLabel from '../../atoms/result-label/result-label';
import Text from '../../atoms/text/text';
import MatchCountdown from '../../molecules/match-countdown/match-countdown';
import MatchData, {
  DATA_CARD_TYPE,
  IMatchData,
  MatchDataVariant,
  STANDARD_TYPE,
} from '../../molecules/match-data/match-data';
import { IMatchRow } from '../../molecules/match-row/match-row';
import Team, { IconPosition, TeamLogoSize } from '../../molecules/team/team';
import './lmp-block.scss';

// FIXME: to move into MATCH-FOLLOW component when refactoring
export const ICON_MATCH_FOLLOW = 'icon-follow';
export const ICON_MATCH_UNFOLLOW = 'icon-unfollow';
export type MatchFollowIcon = 'icon-follow' | 'icon-unfollow';
export const matchFollowIcon: MatchFollowIcon[] = [ICON_MATCH_FOLLOW, ICON_MATCH_UNFOLLOW];
export interface IMatchFollow {
  callback: Function;
  label: string;
  className: MatchFollowIcon;
}

export interface ILmpBlock extends IMatchData {
  ampProps?: IAmpPropsTag[];
  iconPosition?: IconPosition;
  logoSize?: TeamLogoSize;
  matchFollow?: IMatchFollow; // ISS-5690
  resultLabelSize?: ComponentSize;
  variant?: MatchDataVariant;
  countdownEnabled?: boolean;
}

const classNames = (
  colorSchema: ColorSchema,
  hasVideo: boolean,
  isDataCard: boolean,
  isPreMatch: boolean,
  size: ComponentSize,
  matchFollow?: string
) =>
  makeClassNames({
    lmpBlock: ['lmp-block', isDataCard && 'lmp-block--data-card', `${applyColorSchema('lmp-block', colorSchema)}`],
    lmpGrid: ['lmp-block--grid', isDataCard && 'lmp-block--grid--data-card', `lmp-block--grid--${size}`],
    matchWrapper: [
      'lmp-block--match-wrapper',
      hasVideo && 'lmp-block--match-wrapper__video',
      isDataCard && !isPreMatch && 'lmp-block--match-wrapper--centered',
      isDataCard && isPreMatch && 'lmp-block--match-wrapper--prematch',
    ],
    matchData: ['lmp-block--match-data', hasVideo && 'lmp-block--match-data__video'],
    matchResult: ['lmp-block--result', hasVideo && 'lmp-block--result__video'],
    teamHome: 'lmp-block--team-home',
    teamAway: 'lmp-block--team-away',
    textGoals: 'lmp-block--text-goals',
    textWinner: 'lmp-block--text-winner',
    resultLabelContent: ['lmp-block--result-label-content'],
    matchFollowCta: ['lmp-block--match-follow-cta', matchFollow && `lmp-block--match-follow-${matchFollow}`],
  });

const goalsSizeMap: Partial<Record<ComponentSize, FontSize>> = {
  large: '12--14',
  medium: '14',
  small: '14',
  xsmall: '12',
};

const processMatch = (match: IMatchRow): IMatchRow => {
  const matchWithComputedFullTime = computeFullTime(match);
  const matchWithIsLose = addIsLoseOnTeams(matchWithComputedFullTime);

  return matchWithIsLose;
};

const LmpBlock = (lmpBlock: ILmpBlock) => {
  const {
    /* istanbul ignore next: default value */ hasVideo = false,
    /* istanbul ignore next: default value */ colorSchema = 'dark',
    /* istanbul ignore next: default value */ logoSize = '45--60',
    /* istanbul ignore next: default value */ fontSize = '14--22',
    /* istanbul ignore next: default value */ iconPosition = 'vertical',
    /* istanbul ignore next: default value */ variant = STANDARD_TYPE,
    matchFollow,
    ampProps,
    /* istanbul ignore next: default value */ resultLabelSize = 'large',
    countdownEnabled,
  } = lmpBlock;
  const match = processMatch(lmpBlock.match);
  const { status, date } = match;
  const cn = classNames(
    colorSchema,
    hasVideo,
    variant === DATA_CARD_TYPE,
    status === 'PreMatch',
    resultLabelSize,
    matchFollow?.className
  );

  // DESTRUCTURING with both RENAMING and ASSIGNING DEFAULT VALUES
  const {
    /* istanbul ignore next: default value */ name: homeName = '',
    logoUrl: homeLogoUrl = '',
    negativeLogoUrl: homeNegativeLogoUrl,
    homepageUrl,
  } = match.home;
  const {
    /* istanbul ignore next: default value */ name: awayName = '',
    logoUrl: awayLogoUrl = '',
    negativeLogoUrl: awayNegativeLogoUrl,
    homepageUrl: awayPageUrl,
  } = match.away;
  const result = getMatchResult(match, true);
  const goals = getFooter(match);
  const footer = getHeader(match, variant === DATA_CARD_TYPE); // Force "DCR: 3-1" instead of "Inter passa il turno ai rigori"
  const dataState = getMatchState(status);

  return (
    <div className={cn.lmpBlock} data-state={dataState} data-status={slugifyPascalCase(status)}>
      <div className={cn.lmpGrid}>
        <div className={cn.matchData}>
          <MatchData
            variant={variant}
            hasVideo={hasVideo}
            match={match}
            colorSchema={colorSchema}
            size={resultLabelSize}
          />
        </div>
        <div className={cn.teamHome}>
          <Team
            colorSchema={colorSchema}
            name={homeName}
            logoSize={logoSize}
            logoUrl={colorSchema === 'dark' ? homeNegativeLogoUrl ?? homeLogoUrl : homeLogoUrl}
            fontSize={fontSize}
            iconPosition={iconPosition}
            scoreboard={variant === STANDARD_TYPE ? match.home.scoreboard : []}
            isLose={match.home.isLose}
            ampProps={ampProps}
            homepageUrl={homepageUrl}
            clickable
            ellipsis
          />
        </div>
        <div className={cn.matchWrapper}>
          <div className={cn.matchResult}>
            {variant === DATA_CARD_TYPE && status !== 'PreMatch' && (
              <Text
                color={ColorSchemaToColor(colorSchema)}
                fontSize={goalsSizeMap[resultLabelSize] ?? goalsSizeMap.large}
                fontWeight="400"
              >
                {footer}
              </Text>
            )}
            <ResultLabel itemState={status} colorSchema={colorSchema} size={resultLabelSize}>
              <MatchCountdown matchDate={date} matchStatus={status} enabled={countdownEnabled}>
                {result.text}
              </MatchCountdown>
            </ResultLabel>
            <Text
              className={cn.textGoals}
              color={ColorSchemaToColor(colorSchema)}
              fontSize={goalsSizeMap[resultLabelSize] ?? goalsSizeMap.large}
              fontWeight="400"
            >
              {goals}
            </Text>
          </div>
        </div>
        <div className={cn.teamAway}>
          <Team
            colorSchema={colorSchema}
            name={awayName}
            logoSize={logoSize}
            logoUrl={colorSchema === 'dark' ? awayNegativeLogoUrl ?? awayLogoUrl : awayLogoUrl}
            fontSize={fontSize}
            iconPosition={iconPosition}
            scoreboard={variant === STANDARD_TYPE ? match.away.scoreboard : []}
            isLose={match.away.isLose}
            ampProps={ampProps}
            homepageUrl={awayPageUrl}
            clickable
            ellipsis
          />
        </div>
      </div>
      {matchFollow && (
        <div className={cn.matchFollowCta}>
          <span onClick={() => matchFollow.callback(match.id ?? match.matchId)}>{matchFollow.label}</span>
        </div>
      )}
      {variant === STANDARD_TYPE && (
        <div className={cn.textWinner}>
          {/* TODO: Write who won the round, but only when game is over (replacing SKY CHANNEL editorial row) */}
          {(isKnockout(match.competitionPhase, match.competitionPhaseRound) ||
            isSecondLeg(match.competitionPhase, match.competitionPhaseRound)) &&
            isEnded(match.status) &&
            footer && (
              <Text color={ColorSchemaToColor(colorSchema)} fontSize="12--14" fontWeight="500">
                {footer}
              </Text>
            )}
        </div>
      )}
    </div>
  );
};

export default LmpBlock;
